import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  useNavigate,
  type RouterHistory,
} from '@tanstack/react-router';

import { AjustesTela } from '../ajustes/AjustesTela.js';
import { HojeTela } from '../hoje/HojeTela.js';
import { PessoaTela } from '../pessoas/PessoaTela.js';
import { PessoasTela } from '../pessoas/PessoasTela.js';
import { FolhaRegistrar } from '../registrar/FolhaRegistrar.js';
import { RelatorioTela } from '../relatorio/RelatorioTela.js';
import { TerritorioDetalheTela } from '../territorio/TerritorioDetalheTela.js';
import { TerritorioTela } from '../territorio/TerritorioTela.js';
import { Casca } from './Casca.js';
import { NaoEncontrada } from './NaoEncontrada.js';

export const rotaRaiz = createRootRoute({ component: Casca });
const inicio = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/',
  beforeLoad: () => {
    // `redirect()` do TanStack Router devolve um Response, não um Error;
    // é assim que a biblioteca espera que seja lançado.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/hoje' });
  },
});
const hoje = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/hoje',
  // `pessoa` é o id da ficha a que a visita já nasce ligada — é o que a tira
  // "Para visitar hoje" põe no endereço ao ser tocada. Só vale com
  // `registrar=1`: sem a folha aberta ele não teria a que se referir.
  validateSearch: (s: Record<string, unknown>) => {
    if (s['registrar'] !== 1 && s['registrar'] !== '1') return {};
    const pessoa = s['pessoa'];
    return typeof pessoa === 'string' && pessoa !== ''
      ? { registrar: 1 as const, pessoa }
      : { registrar: 1 as const };
  },
  component: Hoje,
});

/**
 * A tela Hoje com a folha Registrar por cima quando a busca pede
 * (`/hoje?registrar=1`).
 *
 * Quem abre e fecha a folha é o **endereço**, não um estado desta função:
 * é o mesmo caminho do atalho da Tela de Início (`/registrar` redireciona
 * para cá) e o gesto de voltar do iOS fecha a folha sem código nenhum.
 *
 * Fechar navega com `replace`: a folha não é uma parada do histórico, e
 * sem isto voltar depois de fechar a reabriria.
 *
 * A `FolhaRegistrar` fica montada mesmo fechada — é ela que carrega o aviso
 * de "Visita registrada" com Desfazer, que só aparece **depois** de a folha
 * fechar.
 */
function Hoje() {
  const busca = hoje.useSearch();
  const navegar = useNavigate();
  return (
    <>
      <HojeTela />
      <FolhaRegistrar
        aberta={'registrar' in busca && busca.registrar === 1}
        {...('pessoa' in busca && busca.pessoa !== undefined
          ? { pessoaId: busca.pessoa }
          : {})}
        aoFechar={() => {
          void navegar({ to: '/hoje', search: {}, replace: true });
        }}
      />
    </>
  );
}
// Atalho da Tela de Início: um segundo ícone apontando para /registrar.
const registrar = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/registrar',
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- ver comentário acima
    throw redirect({ to: '/hoje', search: { registrar: 1 } });
  },
});
const pessoas = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/pessoas',
  component: PessoasTela,
});
const pessoa = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/pessoas/$id',
  component: PessoaTela,
});
const territorio = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/territorio',
  component: TerritorioTela,
});
const territorioDetalhe = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/territorio/$id',
  component: TerritorioDetalheTela,
});
const relatorio = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/relatorio',
  component: RelatorioTela,
});
const ajustes = createRoute({
  getParentRoute: () => rotaRaiz,
  path: '/ajustes',
  component: AjustesTela,
});

/** O catálogo do design system é ferramenta de quem constrói, não tela de
 *  quem usa: em produção a rota simplesmente não existe, e `/ds` cai no
 *  "Página não encontrada" como qualquer endereço inventado.
 *
 *  `lazyRouteComponent` e não importação direta: assim o `Catalogo` e o CSS
 *  dele ficam num pedaço à parte, que o build de produção nunca pede porque
 *  nenhuma rota o referencia.
 *
 *  A função recebe o `dev` em vez de ler `import.meta.env.DEV` por dentro
 *  porque é isso que a torna testável nos dois valores — em Vitest o `DEV`
 *  é sempre verdadeiro, e o caso que importa provar é o outro. */
export function rotasDeDesenvolvimento(dev: boolean) {
  if (!dev) return [];
  return [
    createRoute({
      getParentRoute: () => rotaRaiz,
      path: '/ds',
      component: lazyRouteComponent(
        () => import('../ds/Catalogo.js'),
        'Catalogo',
      ),
    }),
  ];
}

const arvore = rotaRaiz.addChildren([
  inicio,
  hoje,
  registrar,
  pessoas,
  pessoa,
  territorio,
  territorioDetalhe,
  relatorio,
  ajustes,
  ...rotasDeDesenvolvimento(import.meta.env.DEV),
]);

export function criarRoteador(history?: RouterHistory) {
  return createRouter({
    routeTree: arvore,
    basepath: '/',
    ...(history ? { history } : {}),
    defaultPreload: false,
    // Sem isto um deep link antigo (um ícone da Tela de Início de uma
    // versão anterior, por exemplo) cai no "Not Found" em inglês do
    // TanStack, dentro de um app inteiramente em português.
    defaultNotFoundComponent: NaoEncontrada,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof criarRoteador>;
  }
}
